import * as React from 'react';
import { Dropdown, FormControl, FormLabel } from 'react-bootstrap';

import { Button } from '@acceligentllc/storybook';

import Checkbox from 'af-components/Controls/Checkbox';
import Tooltip from 'af-components/Tooltip';

export interface Props {
	id: string;
	options: Metadata[];
	selected: Metadata[];
	labelKey: string;
	renderMenuItem: (option: Nullable<Metadata>, searchText?: string, index?: number) => React.JSX.Element;
	disableErrorMessage?: boolean;
	hasBlankOption?: boolean;
	valueKey?: string;
	filterBy?: string[] | ((option: Nullable<Metadata>, searchText: string) => boolean);
	className?: string;
	containerClassName?: string;
	placeholder?: string;
	alwaysShowErrors?: boolean;
	label?: string;
	tooltip?: string;
	disabled?: boolean;
	fixed?: boolean;
	onValueChange?: (selectedOption: Nullable<Metadata>) => void;
	renderSelected?: (option: Nullable<Metadata>) => React.JSX.Element;
	onLazyLoad?: (isLazyLoaded: boolean) => Promise<void>;
	filterable?: boolean;
	handleChange?: (item, event: Nullable<Metadata>) => void;
	dropdownClassName?: string;
	block?: React.JSX.Element;
	isStandalone?: boolean;
	withCaret?: boolean;
	isWhite?: boolean;
}

// 300 is max height of the dropdowns menu in pixels
// 1.1 increases max height by 10% just to be sure we're not at the bottom of the screen
// 5 for borders, paddings, margins
const MAX_DROPDOWN_MENU_HEIGHT = 300 * 1.1 + 5;

const PlainMultiselectDropdown: React.FC<Props> = (props) => {
	const {
		options = [],
		alwaysShowErrors = false,
		className = 'dropdown-field dropdown-field--multiselect',
		hasBlankOption = false,
		placeholder = '',
		filterable = false,
		selected = [],
		isStandalone = false,
		withCaret = false,
		isWhite = false,
		fixed = false,
		containerClassName,
		disabled,
		id,
		label,
		tooltip,
		dropdownClassName,
		block,
		valueKey,
		labelKey,
		onValueChange,
		onLazyLoad,
		handleChange,
		filterBy,
		renderSelected,
		renderMenuItem,
	} = props;

	const [isLazyLoading, setIsLazyLoading] = React.useState(false);
	const [lazyLoaded, setLazyLoaded] = React.useState(false);
	const [dropup, setDropup] = React.useState(false);
	const [searchText, setSearchText] = React.useState('');
	const [locationX, setLocationX] = React.useState(0);
	const [locationY, setLocationY] = React.useState(0);
	const [width, setWidth] = React.useState(0);
	const [height, setHeight] = React.useState(0);

	const _dropdownToggle = React.useRef<Nullable<HTMLElement>>(null);

	React.useEffect(() => {
		const menuItem = document.getElementById(id);
		if (menuItem) {
			const { x, y, width: _width, height: _height } = menuItem.getBoundingClientRect();
			if (x !== locationX || y !== locationY) {
				setLocationX(x);
				setLocationY(y);
				setWidth(_width);
				setHeight(_height);
			}
		}
	}, [id, locationX, locationY]);

	const defaultHandleChange = React.useCallback(async (item, e) => {
		const isChecked = e.target.checked;
		const calculatedPropName = valueKey ?? labelKey;
		let newSelected: Metadata[] = [];

		if (isChecked) {
			newSelected = selected.concat(item);
		} else {
			newSelected = selected.filter((_option) => _option[calculatedPropName] !== item[calculatedPropName]);
		}

		// on change callback
		onValueChange?.(newSelected);
	}, [labelKey, onValueChange, selected, valueKey]);

	const onToggle = React.useCallback(async (isOpen: boolean) => {
		if (onLazyLoad) {
			const _lazyLoaded = options && options.length > 0;
			setIsLazyLoading(true);
			setLazyLoaded(_lazyLoaded);
			await onLazyLoad(lazyLoaded);
			setIsLazyLoading(false);
		}

		if (isOpen && _dropdownToggle && _dropdownToggle.current) {
			const { height: bodyHeight } = document.body.getClientRects()[0];
			const { y: dropdownTogglePositionY = 0 } = _dropdownToggle.current.getClientRects()[0] || {};

			const isDropup = dropdownTogglePositionY > (bodyHeight - MAX_DROPDOWN_MENU_HEIGHT);
			if (dropup !== isDropup) {
				setDropup(isDropup);
			}
		}
	}, [dropup, lazyLoaded, onLazyLoad, options]);

	const onSearchInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const _searchText = event.target.value;
		setSearchText(_searchText);
	}, []);

	const clearSearchInput = React.useCallback(() => {
		setSearchText('');
	}, []);

	const getClassName = () => {
		return (alwaysShowErrors) ? `${className} dropdown-field--with-error` : className;
	};

	// eslint-disable-next-line @typescript-eslint/no-shadow, @typescript-eslint/no-unused-vars
	const defaultRenderOption = React.useCallback((option: Nullable<Metadata>, searchText?: string, index?: number) => {
		if (option === null) {
			return null;
		}

		return option[labelKey];
	}, [labelKey]);

	const filter = React.useCallback((option: Nullable<Metadata>) => {
		if (!searchText) {
			return true;
		}

		if (Array.isArray(filterBy)) {
			const searchInputs = (searchText || '').replace(/\s\s+/g, ' ').toLowerCase().split(' ');
			const isFiltered = searchInputs.every((_searchInput: string) => {
				return filterBy.some((_field: string) => {
					const value = option?.[_field] || '';
					return value.toLowerCase().includes(_searchInput);
				});
			});
			return isFiltered;
		}

		if (typeof filterBy === 'function') {
			return filterBy(option, searchText);
		}

		return true;
	}, [filterBy, searchText]);

	const dropdownStyle = (): React.CSSProperties => {
		const heightOffset = dropup ? (height - MAX_DROPDOWN_MENU_HEIGHT) : height;
		return fixed
			? {
				position: 'fixed',
				right: `calc(100vw - ${locationX}px - ${width}px - 1px)`,
				top: `calc(${locationY}px + ${heightOffset}px)`,
				bottom: 'auto',
				left: 'auto',
			}
			: {};
	};

	const renderItem = React.useCallback((option: Nullable<Metadata>, index: number) => {

		const calculatedPropName = valueKey ?? labelKey;

		const isChecked = !!selected.find((_option) => _option[calculatedPropName] === option?.[calculatedPropName]);
		const isFiltered = !filterable || filter(option);
		const isDisabled = !!option && option.disabled;

		const render = renderMenuItem ?? defaultRenderOption;

		return isFiltered && (
			<li className="dropdown-menu__multiselect-item" key={`menuItem#${index + 1}`}>
				<a role="button">
					<Checkbox
						handleChange={handleChange ? handleChange.bind(this, option) : defaultHandleChange.bind(this, option)}
						inline={true}
						isChecked={isChecked}
						isDisabled={isDisabled}
						label={render(option, searchText, index)}
					/>
				</a>
			</li>
		);
	}, [defaultHandleChange, defaultRenderOption, filter, filterable, handleChange, labelKey, renderMenuItem, searchText, selected, valueKey]);

	const renderSelectedItems = React.useCallback(() => {
		if (!selected?.length) {
			return <span className="dropdown-toggle__placeholder">{placeholder}</span>;
		}

		const render = renderSelected ?? renderMenuItem;
		if (render) {
			return render(selected);
		}

		// case where [name] is some value but we don't have [propName]
		// e.g. [name]=AM1200 but no [propName], we get the value from options
		if (typeof selected === 'string' || typeof selected === 'number') {
			if (options.length) {
				const calculatedPropName = valueKey ?? labelKey;
				const option = options.find((_option) => _option[calculatedPropName] === selected);
				return option?.[labelKey];
			}
			return selected;
		}
		return selected[labelKey];
	}, [labelKey, options, placeholder, renderMenuItem, renderSelected, selected, valueKey]);

	const selectAll = React.useCallback(() => {
		// const filteredOptions = filterable
		// 	? options?.filter((opt) => filterOption(opt)) ?? []
		// 	: options ?? [];

		// props.controlledMultiSelect ? props.setCurrentSelections(filteredOptions) : setSelections(filteredOptions);

		onValueChange?.(options);
	}, [onValueChange, options]);

	const clearAll = React.useCallback(() => {
		// props.controlledMultiSelect ? props.setCurrentSelections([]) : setSelections([]);
		setSearchText('');
		onValueChange?.([]);
	}, [onValueChange]);

	const renderMultiselectOptions = React.useCallback(() => {
		const targetSelections = selected;
		const selectedLength = targetSelections?.length;
		return (
			<div className="multiselect-options">
				<div className="multiselect-options__count">
					Selected ({selectedLength})
				</div>
				<div className="multiselect-options__controls">
					<Button
						label="Select all"
						onClick={selectAll}
						size="small"
						style="link"
					/>
					<Button
						label="Clear all"
						onClick={clearAll}
						size="small"
						style="link"
					/>
				</div>
			</div>
		);
	}, [clearAll, selected, selectAll]);

	let fromGroupClassName = containerClassName ?? '';
	fromGroupClassName = isStandalone ? `${fromGroupClassName} form-group--standalone` : fromGroupClassName;

	let dropdownToggleClassName = isLazyLoading ? 'dropdown-toggle--lazy-loading' : '';
	dropdownToggleClassName = withCaret ? `${dropdownToggleClassName} dropdown-toggle dropdown-toggle--icon-down` : dropdownToggleClassName;
	dropdownToggleClassName = isWhite ? `${dropdownToggleClassName} dropdown-toggle--white` : dropdownToggleClassName;

	return (
		<div className={fromGroupClassName}>
			{label &&
				<FormLabel>
					{label}
					{tooltip &&
						<Tooltip
							message={tooltip}
							placement="top"
						>
							<span className="icon-info" />
						</Tooltip>
					}
				</FormLabel>
			}
			<Dropdown
				className={dropdownClassName ?? getClassName()}
				drop={dropup ? 'up' : undefined}
				id={id}
				onToggle={onToggle}
			>
				<Dropdown.Toggle
					className={dropdownToggleClassName}
					disabled={disabled}
				>
					<div className="dropdown-menu__selected" ref={_dropdownToggle as React.LegacyRef<HTMLDivElement>}>
						{renderSelectedItems()}
						{withCaret && <span className="icon-down dropdown-menu__with-caret"></span>}
						{isLazyLoading && <div className="rbt-aux"><div className="rbt-loader" /></div>}
					</div>
				</Dropdown.Toggle>
				<Dropdown.Menu
					className="dropdown-menu--scroll-container"
					style={dropdownStyle()}
				>
					{filterable &&
						<Dropdown.Header className="dropdown-item dropdown-menu__search-input-container">
							<input
								className="dropdown-menu__search-input"
								onChange={onSearchInputChange}
								placeholder="Search"
								type="text"
								value={searchText}
							/>
							{
								searchText ?
									<span
										className="dropdown-menu__icon icon-close"
										onClick={clearSearchInput}
										role="button"
									/> :
									<span className="dropdown-menu__icon icon-search" />
							}
						</Dropdown.Header>
					}
					{renderMultiselectOptions()}
					<div className="dropdown-menu__scrollable">
						{isLazyLoading && <Dropdown.Item disabled={true} key="menuItem#0">Loading data...</Dropdown.Item>}
						{hasBlankOption && renderItem(null, -1)}
						{options.map(renderItem)}
					</div>
				</Dropdown.Menu>
			</Dropdown>
			<FormControl.Feedback />
			{block}
		</div>
	);
};

export default PlainMultiselectDropdown;
